/**
 * Contrato do Financeiro INTERNO da empresa (EtiquetaChef SRL) — épico #72:
 * contas bancárias/caixa, lançamentos (extrato), importação por OCR e análise.
 *
 * Origem: #73 — 2026-06-21 19:20 +0200 (contas); #74 lançamentos, #75 import
 * OCR, #76 análise, #77 tags — tudo no mesmo dia; #72 é o épico.
 *
 * Por quê: NÃO confundir com `billing.ts` (PSP / cobrança dos clientes). Aqui
 * mora a contabilidade operacional da própria empresa, e por isso as coleções
 * são ROOT e **não multi-tenant** (`/finance_accounts`, `/finance_entries`,
 * `/finance_imports`): não há `tenantId` em nenhum documento daqui.
 * Categorização de lançamento é só por `tags[]` (campo livre); o conceito de
 * "origem"/fonte foi removido em 2026-06-21 (era peso morto: nenhum relatório
 * agregava por tipo contábil).
 *
 * Contrato: os schemas `*Schema` de documento validam o que as callables leem
 * do Firestore; os `*InputSchema` validam o `request.data` das callables (Zod
 * em toda borda) e são reaproveitados pelo admin para tipar a chamada. Os
 * `*Output`/`*Result` são só tipos — a resposta não é validada no cliente.
 *
 * Usado por: Functions `apps/functions/src/finance/**` (13 callables:
 * `create/update/archiveFinanceAccount`, `create/update/deleteFinanceEntry`,
 * `searchFinanceEntries`, `listFinanceTags`, `parseBankStatementImage`,
 * `confirmStatementImport`, `computeFinanceBalance`, `computeCashflow`); admin
 * web `apps/admin/src/lib/callables.ts`, `lib/firestoreFinance.ts` e as rotas
 * `routes/Finance*Page.tsx` (Overview, Accounts, Entries, Imports, ImportFlow,
 * Balance, Cashflow). Não vai para Android/iOS nem para o contrato gerado —
 * é ferramenta do super-admin.
 *
 * Arquitetura: `firestore.rules` — leitura das três coleções só
 * `isSuperAdmin()`, escrita `false` (tudo via Admin SDK nas callables).
 * Dinheiro SEMPRE em cents inteiros (`*Cents`), a mesma convenção de
 * `billing.ts`; valor de lançamento sempre positivo, o sinal vem de `type`.
 * Datas em epoch ms; o "dia" de uma conta é definido pelo `timeZone` IANA dela
 * (c1e22aafc, 2026-07-02), não pelo fuso do servidor.
 *
 * Dicas: o import (#75) deduplica por `fingerprint` — fórmula em
 * `apps/functions/src/finance/fingerprint.ts`; mudar qualquer campo que entra
 * nela invalida o dedupe contra o histórico. Os schemas aqui têm só `.default()`
 * (valor quando falta), nunca `.catch()` — um documento inválido deve falhar o
 * parse, não ser lido como outro.
 *
 * Issues: #72 (épico), #73, #74, #75, #76, #77, #105 (data ao meio-dia UTC),
 * #133 (imagem grande no OCR).
 */

import { z } from 'zod';
import { CurrencySchema, type Currency } from './schemas';

/* ------------------------------------------------------------------ */
/* Branded IDs                                                          */
/* ------------------------------------------------------------------ */

type Brand<K, T> = K & { readonly __brand: T };

export type FinanceAccountId = Brand<string, 'FinanceAccountId'>;

const nonEmpty = z.string().min(1);

export const FinanceAccountIdSchema = nonEmpty.transform((s) => s as FinanceAccountId);

/**
 * Construtor do branded `FinanceAccountId` (cast, sem validação — quem valida
 * é `FinanceAccountIdSchema`). Origem: #73 — 2026-06-21 19:20 +0200.
 * Usado por: admin `FinanceAccountsPage.tsx`, `FinanceImportFlowPage.tsx`,
 * `FinanceEntryEditorDialog.tsx`, `FinanceAccountEditorDialog.tsx`.
 */
export const FinanceAccountId = (s: string): FinanceAccountId => s as FinanceAccountId;

/* ------------------------------------------------------------------ */
/* Enums                                                               */
/* ------------------------------------------------------------------ */

/** Natureza da conta: bancária ou caixa (dinheiro em espécie). */
export const FinanceAccountKind = {
  BANK: 'BANK',
  CASH: 'CASH',
} as const;
export type FinanceAccountKind = (typeof FinanceAccountKind)[keyof typeof FinanceAccountKind];
export const ALL_FINANCE_ACCOUNT_KINDS = Object.values(FinanceAccountKind) as [
  FinanceAccountKind,
  ...FinanceAccountKind[],
];

/* ------------------------------------------------------------------ */
/* Documentos Firestore                                                */
/* ------------------------------------------------------------------ */

/**
 * `/finance_accounts/{id}` — conta bancária ou caixa.
 *
 * Origem: #73 — 2026-06-21 19:20 +0200.
 *
 * Usado por: Functions `finance/createFinanceAccount.ts`,
 * `updateFinanceAccount.ts`, `computeFinanceBalance.ts`, `computeCashflow.ts`;
 * admin `lib/firestoreFinance.ts` (onSnapshot da tela Contas).
 *
 * Dados — exemplo mínimo:
 *   { "id": "acc-1", "name": "Conta corrente", "bank": "Banco X", "kind": "BANK",
 *     "currency": "EUR", "timeZone": "Europe/Rome", "openingBalanceCents": 150000,
 *     "archived": false, "createdAt": 1750000000000, "createdByUid": "uid-1",
 *     "updatedAt": 1750000000000, "updatedByUid": "uid-1" }
 * `timeZone` é o único campo com `.default()`: doc anterior a 2026-07-02 não o
 * tem gravado. `openingBalanceCents` pode ser negativo (conta já no vermelho).
 */
export const FinanceAccountSchema = z.object({
  id: nonEmpty,
  name: z.string().min(1).max(120),
  /** Nome do banco/instituição (null para caixa em espécie). */
  bank: z.string().max(120).nullable(),
  kind: z.enum(ALL_FINANCE_ACCOUNT_KINDS),
  currency: CurrencySchema,
  /**
   * Fuso IANA da conta (contas podem viver em fusos diferentes). Define o
   * "dia" da conta em cortes diários (ex.: posição do saldo). Docs antigos
   * (pré-campo) não têm o valor gravado — o default cobre a única conta
   * legada (Unicredit, Roma); o doc ganha o campo no próximo update.
   */
  timeZone: z.string().min(1).max(80).default('Europe/Rome'),
  /** Saldo de abertura, em cents inteiros (pode ser negativo). */
  openingBalanceCents: z.number().int(),
  archived: z.boolean(),
  createdAt: z.number().int(),
  createdByUid: nonEmpty,
  updatedAt: z.number().int(),
  updatedByUid: nonEmpty,
});
export type FinanceAccount = z.infer<typeof FinanceAccountSchema>;

/* ------------------------------------------------------------------ */
/* Inputs/outputs — contas                                             */
/* ------------------------------------------------------------------ */

/**
 * Input da callable `createFinanceAccount` (#73). Diferente do documento:
 * `bank` é `optional` (vira `null` no doc) e `openingBalanceCents` default 0
 * no servidor. Usado por: Functions `finance/createFinanceAccount.ts`; admin
 * `lib/callables.ts`. Origem: #73 — 2026-06-21 19:20 +0200.
 */
export const CreateFinanceAccountInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  bank: z.string().trim().max(120).optional(),
  kind: z.enum(ALL_FINANCE_ACCOUNT_KINDS),
  currency: CurrencySchema,
  /** Fuso IANA da conta (validado no servidor via Intl). */
  timeZone: z.string().trim().min(1).max(80),
  openingBalanceCents: z.number().int().optional(),
});
export type CreateFinanceAccountInput = z.infer<typeof CreateFinanceAccountInputSchema>;
export type CreateFinanceAccountOutput = { accountId: string; createdAt: number };

/**
 * Input da callable `updateFinanceAccount` (#73) — patch parcial; `updated[]`
 * no output lista o que mudou de fato e `idempotent` diz se nada mudou.
 * Usado por: Functions `finance/updateFinanceAccount.ts`; admin `lib/callables.ts`.
 * Origem: #73 — 2026-06-21 19:20 +0200.
 */
export const UpdateFinanceAccountInputSchema = z.object({
  accountId: FinanceAccountIdSchema,
  name: z.string().trim().min(1).max(120).optional(),
  bank: z.string().trim().max(120).nullable().optional(),
  kind: z.enum(ALL_FINANCE_ACCOUNT_KINDS).optional(),
  currency: CurrencySchema.optional(),
  timeZone: z.string().trim().min(1).max(80).optional(),
  openingBalanceCents: z.number().int().optional(),
});
export type UpdateFinanceAccountInput = z.infer<typeof UpdateFinanceAccountInputSchema>;
export type UpdateFinanceAccountOutput = {
  accountId: string;
  updated: string[];
  idempotent: boolean;
};

/**
 * Input da callable `archiveFinanceAccount` (#73) — arquivar/desarquivar; conta
 * nunca é apagada (os lançamentos apontam para ela). Usado por: Functions
 * `finance/archiveFinanceAccount.ts`; admin `lib/callables.ts`. Origem: #73 —
 * 2026-06-21 19:20 +0200.
 */
export const ArchiveFinanceAccountInputSchema = z.object({
  accountId: FinanceAccountIdSchema,
  archived: z.boolean(),
});
export type ArchiveFinanceAccountInput = z.infer<typeof ArchiveFinanceAccountInputSchema>;
export type ArchiveFinanceAccountOutput = { accountId: string; archived: boolean };

/* ================================================================== */
/* Lançamentos (PR B #74) — movimentos do extrato                      */
/* ================================================================== */

export type FinanceEntryId = Brand<string, 'FinanceEntryId'>;
export const FinanceEntryIdSchema = nonEmpty.transform((s) => s as FinanceEntryId);
/**
 * Construtor do branded `FinanceEntryId` (cast, sem validação). Origem: #74 —
 * 2026-06-21 20:33 +0200. Usado por: admin `FinanceEntriesPage.tsx` e
 * `FinanceEntryEditorDialog.tsx`.
 */
export const FinanceEntryId = (s: string): FinanceEntryId => s as FinanceEntryId;

/** Sentido do movimento. Valor é sempre positivo; o sinal vem do tipo. */
export const FinanceEntryType = { IN: 'IN', OUT: 'OUT' } as const;
export type FinanceEntryType = (typeof FinanceEntryType)[keyof typeof FinanceEntryType];
export const ALL_FINANCE_ENTRY_TYPES = Object.values(FinanceEntryType) as [
  FinanceEntryType,
  ...FinanceEntryType[],
];

/** Como o lançamento entrou no sistema. */
export const FinanceEntrySource = { MANUAL: 'MANUAL', OCR: 'OCR' } as const;
export type FinanceEntrySource = (typeof FinanceEntrySource)[keyof typeof FinanceEntrySource];
export const ALL_FINANCE_ENTRY_SOURCES = Object.values(FinanceEntrySource) as [
  FinanceEntrySource,
  ...FinanceEntrySource[],
];

const FinanceTagSchema = z.string().trim().min(1).max(40);

/**
 * `/finance_entries/{id}` — um movimento (entrada ou saída).
 *
 * Origem: #74 — 2026-06-21 20:33 +0200.
 *
 * Usado por: Functions `finance/createFinanceEntry.ts`, `updateFinanceEntry.ts`,
 * `searchFinanceEntries.ts`, `listFinanceTags.ts`, `confirmStatementImport.ts`,
 * `computeFinanceBalance.ts`, `computeCashflow.ts`, `fingerprint.ts`. O admin
 * não lê a coleção direto — recebe `FinanceEntry[]` de `searchFinanceEntries`.
 *
 * Dados — exemplo mínimo:
 *   { "id": "ent-1", "accountId": "acc-1", "type": "OUT", "amountCents": 1290,
 *     "currency": "EUR", "date": 1750032000000, "description": "Hospedagem",
 *     "tags": ["infra"], "source": "MANUAL", "importId": null,
 *     "fingerprint": "acc-1|1750032000000|OUT|1290|hospedagem", ... }
 * `date` é gravada ao MEIO-DIA UTC do dia escolhido (#105) para o fingerprint
 * de um lançamento manual e de uma linha de OCR do mesmo dia coincidirem.
 * `fingerprint` = `accountId|dateMs|type|amountCents|descrição normalizada`
 * (`apps/functions/src/finance/fingerprint.ts`).
 */
export const FinanceEntrySchema = z.object({
  id: nonEmpty,
  accountId: nonEmpty,
  type: z.enum(ALL_FINANCE_ENTRY_TYPES),
  /** Valor SEMPRE positivo, em cents inteiros. O sinal vem de `type`. */
  amountCents: z.number().int().positive(),
  /** Denormalizada da conta no momento do lançamento. */
  currency: CurrencySchema,
  /** Data de competência (epoch ms) escolhida pelo usuário. */
  date: z.number().int(),
  description: z.string().min(1).max(280),
  tags: z.array(FinanceTagSchema).max(20),
  source: z.enum(ALL_FINANCE_ENTRY_SOURCES),
  /** Sessão de import (PR C) que originou o lançamento; null se manual. */
  importId: z.string().nullable(),
  /** Hash estável (conta+data+tipo+valor+descrição) para dedupe de import. */
  fingerprint: nonEmpty,
  createdAt: z.number().int(),
  createdByUid: nonEmpty,
  updatedAt: z.number().int(),
  updatedByUid: nonEmpty,
});
export type FinanceEntry = z.infer<typeof FinanceEntrySchema>;

/**
 * Input da callable `createFinanceEntry` (#74). `currency`, `source: MANUAL`,
 * `importId: null` e `fingerprint` são preenchidos no servidor. Usado por:
 * Functions `finance/createFinanceEntry.ts`; admin `lib/callables.ts`. Origem:
 * #74 — 2026-06-21 20:33 +0200.
 */
export const CreateFinanceEntryInputSchema = z.object({
  accountId: FinanceAccountIdSchema,
  type: z.enum(ALL_FINANCE_ENTRY_TYPES),
  amountCents: z.number().int().positive(),
  date: z.number().int(),
  description: z.string().trim().min(1).max(280),
  tags: z.array(FinanceTagSchema).max(20).optional(),
});
export type CreateFinanceEntryInput = z.infer<typeof CreateFinanceEntryInputSchema>;
export type CreateFinanceEntryOutput = { entryId: string; createdAt: number };

/**
 * Input da callable `updateFinanceEntry` (#74) — patch parcial; o servidor
 * recomputa `fingerprint` quando um campo da chave muda. Usado por: Functions
 * `finance/updateFinanceEntry.ts`; admin `lib/callables.ts`. Origem: #74 —
 * 2026-06-21 20:33 +0200.
 */
export const UpdateFinanceEntryInputSchema = z.object({
  entryId: FinanceEntryIdSchema,
  accountId: FinanceAccountIdSchema.optional(),
  type: z.enum(ALL_FINANCE_ENTRY_TYPES).optional(),
  amountCents: z.number().int().positive().optional(),
  date: z.number().int().optional(),
  description: z.string().trim().min(1).max(280).optional(),
  tags: z.array(FinanceTagSchema).max(20).optional(),
});
export type UpdateFinanceEntryInput = z.infer<typeof UpdateFinanceEntryInputSchema>;
export type UpdateFinanceEntryOutput = {
  entryId: string;
  updated: string[];
  idempotent: boolean;
};

/**
 * Input da callable `deleteFinanceEntry` (#74) — exclusão física (não há
 * soft-delete de lançamento). Usado por: Functions `finance/deleteFinanceEntry.ts`;
 * admin `lib/callables.ts`. Origem: #74 — 2026-06-21 20:33 +0200.
 */
export const DeleteFinanceEntryInputSchema = z.object({
  entryId: FinanceEntryIdSchema,
});
export type DeleteFinanceEntryInput = z.infer<typeof DeleteFinanceEntryInputSchema>;
export type DeleteFinanceEntryOutput = { entryId: string; deleted: boolean };

/**
 * Filtros + paginação do extrato. Busca server-side (callable
 * `searchFinanceEntries`, #74).
 *
 * Contrato: a busca lê a janela de data inteira (teto `FINANCE_SEARCH_CAP` =
 * 2000 em `apps/functions/src/finance/constants.ts`), filtra e pagina em
 * memória — `capped: true` no resultado avisa que há lançamentos fora da conta.
 * `page`/`pageSize` têm `.default()`: cliente que não manda recebe a 1ª página
 * de 25.
 *
 * Origem: #74 — 2026-06-21 20:33 +0200.
 *
 * Usado por: Functions `finance/searchFinanceEntries.ts`; admin
 * `lib/callables.ts` e `features/finance-entries/useFinanceEntriesSearch.ts`.
 */
export const SearchFinanceEntriesInputSchema = z.object({
  accountId: z.string().optional(),
  type: z.enum(ALL_FINANCE_ENTRY_TYPES).optional(),
  tag: FinanceTagSchema.optional(),
  currency: CurrencySchema.optional(),
  /** Janela de competência (epoch ms). */
  dateFrom: z.number().int().optional(),
  dateTo: z.number().int().optional(),
  /** Busca textual em descrição + tags. */
  text: z.string().trim().max(120).optional(),
  minCents: z.number().int().optional(),
  maxCents: z.number().int().optional(),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
});
export type SearchFinanceEntriesInput = z.infer<typeof SearchFinanceEntriesInputSchema>;

export type FinanceCurrencyTotals = { inCents: number; outCents: number };
export type SearchFinanceEntriesResult = {
  items: FinanceEntry[];
  /** Total de itens que casam o filtro (dentro do cap). */
  total: number;
  /** Entradas/saídas somadas por moeda, sobre TODO o conjunto filtrado. */
  totalsByCurrency: Record<string, FinanceCurrencyTotals>;
  /** Tags distintas na janela de data, para popular o filtro. */
  allTags: string[];
  /** true se a janela bateu no teto e há lançamentos não considerados. */
  capped: boolean;
};

/* ================================================================== */
/* Tags (PR E #77) — catálogo por tags livres (sem coleção)            */
/* ================================================================== */

/**
 * Lista as tags já usadas nos lançamentos, para autocomplete no editor e
 * filtros. Decisão #72: classificação é por **tags livres** (não há catálogo
 * controlado de categorias). Sem input — varre `finance_entries` (com cap) e
 * devolve as tags distintas ordenadas.
 *
 * Origem: #77 — 2026-06-21 22:59 +0200.
 *
 * Usado por: Functions `finance/listFinanceTags.ts`; admin `lib/callables.ts`.
 */
export const ListFinanceTagsInputSchema = z.object({}).strip();
export type ListFinanceTagsInput = z.infer<typeof ListFinanceTagsInputSchema>;
export type ListFinanceTagsResult = {
  /** Tags distintas já usadas, ordenadas alfabeticamente. */
  tags: string[];
  /** true se a varredura bateu o teto e pode haver tags não listadas. */
  capped: boolean;
};

/* ================================================================== */
/* Import por OCR/LLM (PR C #75) — extrato bancário a partir de print  */
/* ================================================================== */

export type FinanceImportId = Brand<string, 'FinanceImportId'>;
export const FinanceImportIdSchema = nonEmpty.transform((s) => s as FinanceImportId);
/**
 * Construtor do branded `FinanceImportId` (cast, sem validação). Origem: #75 —
 * 2026-06-21 22:25 +0200. Usado por: admin `FinanceImportFlowPage.tsx` (o id
 * da sessão nasce no cliente — é a pasta do upload).
 */
export const FinanceImportId = (s: string): FinanceImportId => s as FinanceImportId;

/**
 * Estado da sessão de import. Nasce `DRAFT` no parse (preview, sem gravar);
 * vira `CONFIRMED` quando os lançamentos confirmados são gravados em lote.
 */
export const FinanceImportStatus = { DRAFT: 'DRAFT', CONFIRMED: 'CONFIRMED' } as const;
export type FinanceImportStatus = (typeof FinanceImportStatus)[keyof typeof FinanceImportStatus];
export const ALL_FINANCE_IMPORT_STATUSES = Object.values(FinanceImportStatus) as [
  FinanceImportStatus,
  ...FinanceImportStatus[],
];

/**
 * `/finance_imports/{id}` — uma sessão de import por OCR. Guarda o ponteiro
 * pra imagem no Storage (super-admin only) e os contadores do último parse.
 * Não persiste as linhas: reabrir a sessão re-roda o parse sobre a imagem.
 *
 * Origem: #75 — 2026-06-21 22:25 +0200.
 *
 * Usado por: Functions `finance/parseBankStatementImage.ts` (upsert `DRAFT`) e
 * `confirmStatementImport.ts` (vira `CONFIRMED`); admin `lib/firestoreFinance.ts`
 * (onSnapshot da tela Importações).
 *
 * Dados: `imagePath` tem de estar sob `finance_imports/{importId}/` — é o
 * prefixo que `storage.rules` libera só para super-admin e que a callable
 * recusa com `invalid-argument` fora dele. O `id` da sessão é gerado pelo
 * CLIENTE (é a pasta do upload), o que torna o parse reentrante.
 */
export const FinanceImportSchema = z.object({
  id: nonEmpty,
  accountId: nonEmpty,
  /** Caminho no Storage da imagem do extrato (`finance_imports/{id}/...`). */
  imagePath: nonEmpty,
  /** Moeda denormalizada da conta no momento do import. */
  currency: CurrencySchema,
  status: z.enum(ALL_FINANCE_IMPORT_STATUSES),
  /** Linhas que o LLM extraiu no último parse. */
  parsedCount: z.number().int().nonnegative(),
  /** Linhas novas (não casaram com `finance_entries`). */
  newCount: z.number().int().nonnegative(),
  /** Linhas que já existiam (dedupe por fingerprint). */
  dupCount: z.number().int().nonnegative(),
  /** Quantos lançamentos foram efetivamente gravados ao confirmar. */
  confirmedCount: z.number().int().nonnegative(),
  createdAt: z.number().int(),
  createdByUid: nonEmpty,
  updatedAt: z.number().int(),
  updatedByUid: nonEmpty,
  /** Quando a sessão foi confirmada (null enquanto DRAFT). */
  confirmedAt: z.number().int().nullable(),
});
export type FinanceImport = z.infer<typeof FinanceImportSchema>;

/**
 * Uma linha do extrato já marcada para o preview. `fingerprint` cruza com
 * `finance_entries` (mesma fórmula do lançamento manual); `isDuplicate` =
 * já existe (ou repetida dentro do próprio extrato). `isNew = !isDuplicate`.
 *
 * Origem: #75 — 2026-06-21 22:25 +0200.
 *
 * Usado por: só como TIPO (`StatementLinePreview`) em Functions
 * `finance/statementOcr.ts` e `parseBankStatementImage.ts`; o schema Zod em si
 * não valida nada em 2026-10-04 — a saída do LLM é validada pelo schema interno
 * do `statementOcr.ts`.
 */
export const StatementLinePreviewSchema = z.object({
  /** Data de competência (epoch ms) derivada do extrato. */
  date: z.number().int(),
  description: z.string().min(1).max(280),
  /** Valor SEMPRE positivo, em cents inteiros. Sinal vem de `type`. */
  amountCents: z.number().int().positive(),
  type: z.enum(ALL_FINANCE_ENTRY_TYPES),
  fingerprint: nonEmpty,
  isNew: z.boolean(),
  isDuplicate: z.boolean(),
});
export type StatementLinePreview = z.infer<typeof StatementLinePreviewSchema>;

/**
 * Input da callable `parseBankStatementImage` (#75) — só PREVIEW: grava a
 * sessão `DRAFT` e devolve as linhas marcadas, sem criar lançamento.
 * Usado por: Functions `finance/parseBankStatementImage.ts`; admin `lib/callables.ts`.
 * Origem: #75 — 2026-06-21 22:25 +0200.
 */
export const ParseBankStatementImageInputSchema = z.object({
  /** Id da sessão gerado pelo cliente (= pasta da imagem no Storage). */
  importId: FinanceImportIdSchema,
  accountId: FinanceAccountIdSchema,
  /** Caminho no Storage onde o cliente subiu a imagem. */
  imagePath: nonEmpty.max(300),
});
export type ParseBankStatementImageInput = z.infer<typeof ParseBankStatementImageInputSchema>;
export type ParseBankStatementImageOutput = {
  importId: string;
  accountId: string;
  currency: Currency;
  lines: StatementLinePreview[];
  parsedCount: number;
  newCount: number;
  dupCount: number;
};

/**
 * Uma linha confirmada (já editável) que vira `finance_entries`. Origem: #75 —
 * 2026-06-21 22:25 +0200. Usado por: `ConfirmStatementImportInputSchema.entries`.
 */
export const ConfirmStatementEntrySchema = z.object({
  type: z.enum(ALL_FINANCE_ENTRY_TYPES),
  amountCents: z.number().int().positive(),
  date: z.number().int(),
  description: z.string().trim().min(1).max(280),
  tags: z.array(FinanceTagSchema).max(20).optional(),
});
export type ConfirmStatementEntry = z.infer<typeof ConfirmStatementEntrySchema>;

/**
 * Input da callable `confirmStatementImport` (#75) — grava em lote as linhas
 * que o usuário manteve; dedupe por fingerprint repete-se NA GRAVAÇÃO
 * (`skipped`), porque o preview pode estar velho. Usado por: Functions
 * `finance/confirmStatementImport.ts`; admin `lib/callables.ts`. Origem: #75 —
 * 2026-06-21 22:25 +0200.
 */
export const ConfirmStatementImportInputSchema = z.object({
  importId: FinanceImportIdSchema,
  /** Lançamentos que o usuário manteve no preview (já podem estar editados). */
  entries: z.array(ConfirmStatementEntrySchema).min(1).max(500),
});
export type ConfirmStatementImportInput = z.infer<typeof ConfirmStatementImportInputSchema>;
export type ConfirmStatementImportOutput = {
  importId: string;
  /** Quantos lançamentos foram efetivamente gravados. */
  created: number;
  /** Quantos foram pulados por já existirem (dedupe na hora da gravação). */
  skipped: number;
};

/* ================================================================== */
/* Análise (PR D #76) — saldo, balanço e fluxo de caixa                */
/* ================================================================== */

/** Totais de fluxo (entrada/saída/líquido) em cents, sobre uma dimensão. */
export type FinanceFlowTotals = { inCents: number; outCents: number; netCents: number };

/**
 * Quebra por tag (decisão #72 = tags livres). Um lançamento multi-tag aparece
 * em CADA tag (dupla contagem intencional), então a soma das linhas ≠ total
 * geral. `tag: ''` agrupa os lançamentos **sem nenhuma tag** ("Sem tag").
 */
export type FinanceTagBreakdownItem = {
  tag: string;
  totalsByCurrency: Record<string, FinanceFlowTotals>;
};

/* ---- computeFinanceBalance: posição por conta/moeda/tag ---- */

/**
 * Input da callable `computeFinanceBalance` (#76) — posição até `asOf`.
 * Usado por: Functions `finance/computeFinanceBalance.ts`; admin
 * `lib/callables.ts` (`FinanceBalancePage`). Origem: #76 — 2026-06-21 23:07 +0200.
 */
export const ComputeFinanceBalanceInputSchema = z.object({
  /** Posição até esta data (epoch ms, inclusive). Default = agora (servidor). */
  asOf: z.number().int().optional(),
  accountId: z.string().optional(),
  currency: CurrencySchema.optional(),
});
export type ComputeFinanceBalanceInput = z.infer<typeof ComputeFinanceBalanceInputSchema>;

export type FinanceAccountBalance = {
  accountId: string;
  currency: Currency;
  openingBalanceCents: number;
  inCents: number;
  outCents: number;
  /** opening + in − out, até `asOf`. */
  balanceCents: number;
};

export type FinanceCurrencyBalance = {
  currency: Currency;
  openingBalanceCents: number;
  inCents: number;
  outCents: number;
  balanceCents: number;
};

export type ComputeFinanceBalanceResult = {
  /** Data efetiva da posição (epoch ms). */
  asOf: number;
  byAccount: FinanceAccountBalance[];
  byCurrency: FinanceCurrencyBalance[];
  /** Fluxo por tag até `asOf` (sem saldo de abertura — opening não tem tag). */
  byTag: FinanceTagBreakdownItem[];
  capped: boolean;
};

/* ---- computeCashflow: séries no tempo + acumulado ---- */

export const FinanceCashflowGranularity = {
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
} as const;
export type FinanceCashflowGranularity =
  (typeof FinanceCashflowGranularity)[keyof typeof FinanceCashflowGranularity];
export const ALL_FINANCE_CASHFLOW_GRANULARITIES = Object.values(FinanceCashflowGranularity) as [
  FinanceCashflowGranularity,
  ...FinanceCashflowGranularity[],
];

/**
 * Input da callable `computeCashflow` (#76) — séries por período + acumulado.
 * `granularity` tem `.default('MONTH')`. Usado por: Functions
 * `finance/computeCashflow.ts`; admin `lib/callables.ts` (`FinanceCashflowPage`).
 * Origem: #76 — 2026-06-21 23:07 +0200.
 */
export const ComputeCashflowInputSchema = z.object({
  dateFrom: z.number().int(),
  dateTo: z.number().int(),
  granularity: z.enum(ALL_FINANCE_CASHFLOW_GRANULARITIES).default('MONTH'),
  accountId: z.string().optional(),
  currency: CurrencySchema.optional(),
  /** Filtro de tags por UNIÃO: o lançamento entra se tiver QUALQUER uma. */
  tags: z.array(FinanceTagSchema).max(20).optional(),
});
export type ComputeCashflowInput = z.infer<typeof ComputeCashflowInputSchema>;

export type FinanceCashflowBucket = {
  /** Chave do período: `YYYY-MM-DD` (dia/semana=segunda) ou `YYYY-MM` (mês). */
  periodKey: string;
  /** Início do período (epoch ms, UTC). */
  periodStart: number;
  /** Fluxo do período, por moeda. */
  totalsByCurrency: Record<string, FinanceFlowTotals>;
  /** Saldo acumulado ao FIM do período, por moeda (ver semântica na spec). */
  accumulatedByCurrency: Record<string, number>;
  /** true se o período está no futuro (previsto) relativo a `now`. */
  isForecast: boolean;
};

export type ComputeCashflowResult = {
  granularity: FinanceCashflowGranularity;
  /** Períodos com ao menos um lançamento, ordenados por `periodStart`. */
  buckets: FinanceCashflowBucket[];
  /** Total do intervalo [dateFrom, dateTo], por moeda. */
  totalsByCurrency: Record<string, FinanceFlowTotals>;
  /** Quebra por tag no intervalo (união respeitada; inclui 'Sem tag'). */
  byTag: FinanceTagBreakdownItem[];
  /** Saldo acumulado inicial (antes de dateFrom), por moeda. */
  openingByCurrency: Record<string, number>;
  capped: boolean;
};
